/**
 * ============================================================================
 * DELIVERY PROOF COMPONENT - 100% Ready
 * ============================================================================
 * 
 * Displays delivery proof (files, links, etc.)
 */

import React from 'react';
import { useI18n } from '../../providers';
import Card from '../common/Card';
import Button from '../common/Button';

/**
 * Delivery Proof Component
 * @param {Object} props
 * @param {Array} props.deliverables - Array of deliverable files/links
 * @param {string} props.deliverablesNotes - Notes about deliverables
 * @returns {JSX.Element}
 */
const DeliveryProof = ({ deliverables = [], deliverablesNotes }) => {
  const { language } = useI18n();

  if (!deliverables || deliverables.length === 0) {
    return (
      <Card variant="glass" className="p-12 text-center">
        <p className="text-gray-500 dark:text-gray-400">
          {language === 'fr' ? 'Aucune preuve de livraison' : 'No delivery proof'}
        </p>
      </Card>
    );
  }

  const getFileIcon = (url, name) => {
    if (!url && !name) return '📎';
    const extension = (name || url).split('.').pop()?.toLowerCase();
    const imageExts = ['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg'];
    const videoExts = ['mp4', 'avi', 'mov', 'wmv', 'flv'];
    const docExts = ['pdf', 'doc', 'docx'];
    const codeExts = ['js', 'jsx', 'ts', 'tsx', 'html', 'css', 'json'];

    if (imageExts.includes(extension)) return '🖼️';
    if (videoExts.includes(extension)) return '🎥';
    if (docExts.includes(extension)) return '📄';
    if (codeExts.includes(extension)) return '💻';
    if (url?.startsWith('http')) return '🔗';
    return '📎';
  };

  const getFileType = (url, name) => {
    if (!url && !name) return 'file';
    if (url?.startsWith('http')) return 'link';
    return 'file';
  };

  return (
    <Card variant="glass" className="animate-fadeInUp">
      <h3 className="text-xl font-black text-gray-900 dark:text-white mb-6 flex items-center gap-2">
        <span className="text-2xl">📦</span>
        {language === 'fr' ? 'Preuve de livraison' : 'Delivery Proof'}
      </h3>
      <div className="space-y-4">
        {deliverables.map((deliverable, index) => {
          const url = deliverable.url || deliverable;
          const name = deliverable.name || `File ${index + 1}`;
          const type = getFileType(url, name);
          const icon = getFileIcon(url, name);

          return (
            <Card
              key={index}
              variant="outlined"
              hover
              className="bg-white/80 dark:bg-gray-800/80 animate-fadeInUp"
              style={{ animationDelay: `${index * 0.1}s` }}
            >
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-blue-500 to-cyan-500 flex items-center justify-center text-2xl shadow-lg flex-shrink-0">
                  {icon}
                </div>
                <div className="flex-1 min-w-0">
                  <h4 className="font-black text-gray-900 dark:text-white truncate mb-1">
                    {name}
                  </h4>
                  {type === 'link' && (
                    <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
                      {url}
                    </p>
                  )}
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    if (type === 'link') {
                      window.open(url, '_blank', 'noopener,noreferrer');
                    } else {
                      // Download file
                      const link = document.createElement('a');
                      link.href = url;
                      link.target = '_blank';
                      link.rel = 'noopener noreferrer';
                      link.click();
                    }
                  }}
                  className="flex-shrink-0"
                >
                  {type === 'link'
                    ? language === 'fr'
                      ? 'Ouvrir'
                      : 'Open'
                    : language === 'fr'
                    ? 'Télécharger'
                    : 'Download'}{' '}
                  →
                </Button>
              </div>
            </Card>
          );
        })}

        {/* Deliverables Notes */}
        {deliverablesNotes && (
          <Card variant="outlined" className="bg-gray-50 dark:bg-gray-800/50 mt-4">
            <p className="text-sm font-medium text-gray-600 dark:text-gray-400 mb-2">
              {language === 'fr' ? 'Notes du vendeur' : "Seller's Notes"}
            </p>
            <p className="text-sm text-gray-700 dark:text-gray-300 whitespace-pre-wrap">
              {deliverablesNotes}
            </p>
          </Card>
        )}
      </div>
    </Card>
  );
};

export default DeliveryProof;
